<script>
    /* States */
    import { explorerState } from "@app/store/explorerStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
    import { showModal } from "@app/store/modalStore.svelte.js";
    import { VList } from "virtua/svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { saveLastExplorerLocationFromRecord } from "@app/lib/explorerLastLocation.js";
    import * as m from "@app/paraglide/messages.js";

    /* Firebase */
    import { auth, functions } from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";
    import { collection, doc, getDoc, getDocs, query as fsQuery, where, orderBy, limit, Timestamp } from "firebase/firestore";
    import { ref, getDownloadURL } from "firebase/storage";
    import { httpsCallable } from "firebase/functions";

    /* Search */
    import { searchStorageRecords } from "@app/lib/storageSearch.js";

    const iconMap = {
        folder: "bi-folder-fill",
        project: "bi-file-earmark-play",
        "team project": "bi-people",
        "work order": "bi-clipboard-check",
    };

    const typeOptions = ["all", "folder", "project", "team project", "work order"];

    const userId = auth.currentUser.uid;
    const userEmail = auth.currentUser.email;
    const teamId = authState.team ? authState.team.id : null;
    const homeDbRef = collection(db, "users", userId, "storage");
    const teamDbRef = teamId
        ? collection(db, "teams", teamId, "storage")
        : null;
    const storageRef = ref(storage);
    const homeStorageBasePath = `users/${userId}/projects/`;
    const teamStorageBasePath = teamId ? `teams/${teamId}/storage/` : "";

    let query = $state("");
    let scope = $state("all");
    let type = $state("all");
    let starredOnly = $state(false);
    let createdFrom = $state("");
    let createdTo = $state("");
    let updatedFrom = $state("");
    let updatedTo = $state("");
    let searchPage = $state(0);
    let totalHits = $state(0);
    let totalPages = $state(0);
    let searched = $state(false);
    let searchLoading = $state(false);
    let operationLoading = $state(false);
    let lastError = $state("");

    const hasTeam = $derived(Boolean(authState.team?.id));
    const hasSearched = $derived(searched);
    const hasMore = $derived(totalPages > 0 && searchPage + 1 < totalPages);
    const effectiveScope = $derived(!hasTeam && scope === "team" ? "all" : scope);

    function normalizeRootDir(record = {}) {
        if (record.rootDir === "personal" || record.rootDir === "team") {
            return record.rootDir;
        }

        return record.scope === "team" ? "team" : "personal";
    }

    function getRecordId(record = {}) {
        if (record.id) {
            return record.id;
        }

        if (record.recordId) {
            return record.recordId;
        }

        if (typeof record.objectID === "string" && record.objectID.includes(":")) {
            return record.objectID.split(":").pop();
        }

        return record.objectID;
    }

    function toTimestamp(value) {
        if (!value || typeof value?.toDate === "function") {
            return value || null;
        }

        if (typeof value === "number") {
            return Timestamp.fromMillis(value);
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime())
            ? null
            : Timestamp.fromDate(date);
    }

    function normalizeRecord(record) {
        return {
            ...record,
            id: getRecordId(record),
            rootDir: normalizeRootDir(record),
            createdOnMillis: record.createdOnMillis || record.createdOn || null,
            updatedOnMillis: record.updatedOnMillis || record.updatedOn || null,
            createdOn: toTimestamp(record.createdOn),
            updatedOn: toTimestamp(record.updatedOn),
        };
    }

    function buildFilters() {
        return {
            scope: effectiveScope,
            type,
            starredOnly,
            createdFrom: createdFrom || undefined,
            createdTo: createdTo || undefined,
            updatedFrom: updatedFrom || undefined,
            updatedTo: updatedTo || undefined,
        };
    }

    async function runSearch({ append = false } = {}) {
        if (searchLoading) return;

        try {
            searchLoading = true;
            explorerState.loading = true;
            lastError = "";

            const nextPage = append ? searchPage + 1 : 0;
            const result = await searchStorageRecords({
                query,
                filters: buildFilters(),
                page: nextPage,
            });
            const records = result.records.map(normalizeRecord);

            explorerState.records = append
                ? [...explorerState.records, ...records]
                : records;
            if (!append) {
                explorerState.selectedRecords = [];
            }
            searched = true;
            searchPage = result.page;
            totalPages = result.nbPages;
            totalHits = result.nbHits;
        } catch (error) {
            console.error("Error searching storage records:", error);
            lastError = error.message;
            toast.push(m.explorer_search_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            searchLoading = false;
            explorerState.loading = false;
        }
    }

    function resetSearch() {
        query = "";
        scope = "all";
        type = "all";
        starredOnly = false;
        createdFrom = "";
        createdTo = "";
        updatedFrom = "";
        updatedTo = "";
        searchPage = 0;
        totalHits = 0;
        totalPages = 0;
        searched = false;
        lastError = "";
        explorerState.records = [];
        explorerState.selectedRecords = [];
    }

    function selectRecord(clickEvent, id) {
        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            explorerState.selectedRecords = explorerState.selectedRecords.includes(id)
                ? explorerState.selectedRecords.filter((recordId) => recordId !== id)
                : [...explorerState.selectedRecords, id];
            return;
        }

        if (clickEvent.shiftKey && explorerState.selectedRecords.length > 0) {
            const lastSelectedRecord =
                explorerState.selectedRecords[explorerState.selectedRecords.length - 1];
            const lastSelectedRecordIndex = explorerState.records.findIndex(
                (record) => record.id === lastSelectedRecord,
            );
            const currentRecordIndex = explorerState.records.findIndex(
                (record) => record.id === id,
            );

            if (lastSelectedRecordIndex !== -1 && currentRecordIndex !== -1) {
                const recordsToSelect = explorerState.records.slice(
                    Math.min(lastSelectedRecordIndex, currentRecordIndex),
                    Math.max(lastSelectedRecordIndex, currentRecordIndex) + 1,
                );
                explorerState.selectedRecords = recordsToSelect.map((record) => record.id);
                return;
            }
        }

        explorerState.selectedRecords = [id];
    }

    function canUserAccessFolder(folder) {
        if (
            authState.user &&
            (authState.user.role === "admin" || authState.user.role === "manager")
        ) {
            return true;
        }

        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }

        if (authState.user?.groups) {
            return folder.groups.some((groupId) => authState.user.groups.includes(groupId));
        }

        return false;
    }

    async function getRecords(folderId = null) {
        try {
            explorerState.loading = true;
            explorerState.records = [];
            explorerState.selectedRecords = [];

            const dbRef = explorerState.rootDir === "personal" ? homeDbRef : teamDbRef;
            if (!dbRef) {
                return;
            }

            const querySnapshot = await getDocs(fsQuery(dbRef,
                where("locationId", "==", folderId),
                orderBy("type"),
                orderBy("name", "asc")));
            const records = [];

            querySnapshot.forEach((doc) => {
                const record = { ...doc.data(), rootDir: explorerState.rootDir };

                if (
                    explorerState.rootDir === "team" &&
                    record.type === "folder" &&
                    !canUserAccessFolder(record)
                ) {
                    return;
                }

                records.push(record);
            });

            explorerState.records = records;
        } catch (error) {
            console.error("Error loading folder records:", error);
            toast.push(error.message, {
                classes: ["toast-danger"],
            });
        } finally {
            explorerState.loading = false;
        }
    }

    async function buildFolderNavigationPath(folder) {
        explorerState.navHistory = [];

        if (!folder) {
            return;
        }

        const dbRef = folder.rootDir === "personal" ? homeDbRef : teamDbRef;
        if (!dbRef) {
            throw new Error(`Invalid database reference for rootDir: ${folder.rootDir}`);
        }

        if (!folder.locationId) {
            explorerState.navHistory = [folder];
            return;
        }

        const folderPath = [];
        let currentFolderId = folder.locationId;
        let safetyCounter = 0;
        const maxDepth = 20;

        while (currentFolderId && safetyCounter < maxDepth) {
            safetyCounter += 1;
            const parentDoc = await getDoc(doc(dbRef, currentFolderId));

            if (!parentDoc.exists()) {
                break;
            }

            const parentFolder = {
                ...parentDoc.data(),
                id: parentDoc.data().id || parentDoc.id,
                rootDir: folder.rootDir,
            };
            folderPath.unshift(parentFolder);
            currentFolderId = parentFolder.locationId;
        }

        folderPath.push(folder);
        explorerState.navHistory = folderPath;
    }

    async function openRecord(record) {
        if (!record || operationLoading) return;

        if (record.type === "folder") {
            try {
                operationLoading = true;
                const rootDir = normalizeRootDir(record);
                const dbRef = rootDir === "personal" ? homeDbRef : teamDbRef;

                if (!dbRef) {
                    throw new Error(`Invalid database reference for rootDir: ${rootDir}`);
                }

                const folderId = getRecordId(record);
                const folderDoc = await getDoc(doc(dbRef, folderId));
                const folder = folderDoc.exists()
                    ? {
                        ...folderDoc.data(),
                        id: folderDoc.data().id || folderDoc.id,
                        rootDir,
                    }
                    : {
                        ...record,
                        id: folderId,
                        rootDir,
                    };

                await buildFolderNavigationPath(folder);
                explorerState.rootDir = rootDir;
                explorerState.view = "table";
                await getRecords(folder.id);
            } catch (error) {
                console.error("Error opening search folder:", error);
                toast.push(m.explorer_search_folder_open_failed_toast({ error: error.message }), {
                    classes: ["toast-danger"],
                });
            } finally {
                operationLoading = false;
            }

            return;
        }

        if (explorerState.context === "save") {
            toast.push(m.explorer_search_save_context_unavailable_toast(), {
                classes: ["toast-info"],
            });
            return;
        }

        if (
            record.type === "project" ||
            record.type === "team project" ||
            record.type === "work order"
        ) {
            await loadProject(record);
        }
    }

    async function loadProject(record) {
        try {
            operationLoading = true;

            if (record.locked && record.lockedBy !== userEmail) {
                await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.ok,
                    title: m.explorer_table_locked_title(),
                    text: m.explorer_table_locked_text({ lockedBy: record.lockedBy }),
                    icon: "info",
                    confirmButtonText: m.explorer_table_locked_confirm(),
                });
            }

            const projectFilePath =
                record.rootDir === "team"
                    ? `${teamStorageBasePath}${record.id}`
                    : `${homeStorageBasePath}${record.id}`;
            const url =
                record.rootDir === "team"
                    ? (await httpsCallable(functions, "v8GetDownloadLink")(projectFilePath)).data
                    : await getDownloadURL(ref(storageRef, projectFilePath));
            const response = await fetch(url);
            const fileData = await response.json();
            fileData.rootDir = record.rootDir;

            if (record.type === "work order") {
                openWorkOrder(fileData, record);
            } else if (record.type === "project") {
                openProjectFile(fileData, record);
            } else if (record.type === "team project") {
                await openTeamProjectFile(fileData, record);
            }
        } catch (error) {
            console.error(`Error opening ${record.type}:`, error);
            toast.push(m.explorer_table_open_failed_toast({ type: record.type, error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }

    function openProjectFile(description, record) {
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);

        setExternalDataState({
            platform: "Project File",
            status: "importing",
            rawDescription: JSON.stringify(description),
        });

        showModal("loadProjectFile");
    }

    async function openTeamProjectFile(description, selectedRecord) {
        try {
            const projectTeamId = description.teamId;

            if (!projectTeamId || !teamId) {
                throw new Error(m.explorer_search_missing_team_project_id_error());
            }

            const snapshotsRef = collection(db, "teams", teamId, "projects", projectTeamId, "snapshots");
            const snapshotsQuery = await getDocs(fsQuery(snapshotsRef, limit(1)));

            if (snapshotsQuery.empty) {
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }

            const sharesQuery = await getDocs(fsQuery(
                collection(db, "teams", teamId, "shares"),
                where("projectTeamId", "==", projectTeamId),
                limit(1)));

            if (sharesQuery.empty) {
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }

            const shareId = sharesQuery.docs[0].id;
            const url = new URL(window.location.href);
            url.searchParams.set("id", shareId);
            window.history.pushState({}, "", url);

            toast.push(m.explorer_table_open_team_project_toast(), {
                classes: ["toast-info"],
            });

            saveLastExplorerLocationFromRecord(userId, selectedRecord);
            showModal("teamProjectImport");
        } catch (error) {
            console.error("Error processing team project:", error);
            fallbackToProjectFileImport(description, selectedRecord);
        }
    }

    function fallbackToProjectFileImport(description, record) {
        toast.push(m.explorer_table_fallback_project_import_toast(), {
            classes: ["toast-info"],
        });

        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);

        setExternalDataState({
            platform: "Project File",
            status: "importing",
            rawDescription: JSON.stringify(description),
        });

        showModal("loadProjectFile");
    }

    function openWorkOrder(description, record) {
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);

        setExternalDataState({
            platform: "Work Order",
            status: "importing",
            rawDescription: JSON.stringify(description),
        });

        showModal("workOrderImport");
    }

    function formatDate(value) {
        if (!value) return "-";
        if (typeof value?.toDate === "function") {
            return value.toDate().toLocaleDateString();
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
    }

    function recordLocation(record) {
        return record.rootDir === "team" ? m.explorer_search_location_team() : m.explorer_search_location_personal();
    }
</script>

<div id="workspace-search-view" class="d-flex flex-column h-100">
    <form class="card border-0 shadow-sm mb-3" onsubmit={(event) => { event.preventDefault(); runSearch(); }}>
        <div class="card-body p-3">
            <div class="row g-2 align-items-end">
                <div class="col-12 col-xl-4">
                    <label class="form-label small text-muted" for="storage-search-query">{m.explorer_search_label()}</label>
                    <input
                        id="storage-search-query"
                        class="form-control form-control-sm"
                        type="search"
                        placeholder={m.explorer_search_placeholder()}
                        bind:value={query} />
                </div>

                <div class="col-6 col-xl-2">
                    <label class="form-label small text-muted" for="storage-search-scope">{m.explorer_search_scope_label()}</label>
                    <select id="storage-search-scope" class="form-select form-select-sm" bind:value={scope}>
                        <option value="all">{m.explorer_search_scope_all()}</option>
                        <option value="personal">{m.explorer_search_scope_personal()}</option>
                        {#if hasTeam}
                            <option value="team">{m.explorer_search_scope_team()}</option>
                        {/if}
                    </select>
                </div>

                <div class="col-6 col-xl-2">
                    <label class="form-label small text-muted" for="storage-search-type">{m.explorer_search_type_label()}</label>
                    <select id="storage-search-type" class="form-select form-select-sm" bind:value={type}>
                        {#each typeOptions as typeOption (typeOption)}
                            <option value={typeOption}>{typeOption === "all" ? m.explorer_search_type_all() : typeOption}</option>
                        {/each}
                    </select>
                </div>

                <div class="col-12 col-xl-4">
                    <div class="row g-2">
                        <div class="col-6">
                            <label class="form-label small text-muted" for="storage-created-from">{m.explorer_search_created_from()}</label>
                            <input id="storage-created-from" class="form-control form-control-sm" type="date" bind:value={createdFrom} />
                        </div>
                        <div class="col-6">
                            <label class="form-label small text-muted" for="storage-created-to">{m.explorer_search_created_to()}</label>
                            <input id="storage-created-to" class="form-control form-control-sm" type="date" bind:value={createdTo} />
                        </div>
                    </div>
                </div>

                <div class="col-12 col-xl-4">
                    <div class="row g-2">
                        <div class="col-6">
                            <label class="form-label small text-muted" for="storage-updated-from">{m.explorer_search_updated_from()}</label>
                            <input id="storage-updated-from" class="form-control form-control-sm" type="date" bind:value={updatedFrom} />
                        </div>
                        <div class="col-6">
                            <label class="form-label small text-muted" for="storage-updated-to">{m.explorer_search_updated_to()}</label>
                            <input id="storage-updated-to" class="form-control form-control-sm" type="date" bind:value={updatedTo} />
                        </div>
                    </div>
                </div>

                <div class="col-12 col-xl-3">
                    <div class="form-check mt-2">
                        <input id="storage-starred-only" class="form-check-input" type="checkbox" bind:checked={starredOnly} />
                        <label class="form-check-label" for="storage-starred-only">{m.explorer_search_starred_only()}</label>
                    </div>
                </div>

                <div class="col-12 col-xl-5 text-xl-end">
                    <button class="btn btn-sm btn-primary me-2" type="submit" disabled={searchLoading}>
                        {#if searchLoading}
                            <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                        {:else}
                            <i class="bi bi-search me-1" aria-hidden="true"></i>
                        {/if}
                        {m.explorer_search_button()}
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" type="button" onclick={resetSearch} disabled={searchLoading}>
                        {m.explorer_search_reset_button()}
                    </button>
                </div>
            </div>
        </div>
    </form>

    <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="small text-muted">
            {#if lastError}
                <span class="text-danger">{lastError}</span>
            {:else if hasSearched}
                {m.explorer_search_results_count({
                    shown: explorerState.records.length,
                    total: totalHits,
                    suffix: totalHits === 1 ? "" : "s"
                })}
            {:else}
                {m.explorer_search_prompt()}
            {/if}
        </div>
        {#if operationLoading}
            <div class="small text-muted">
                <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                {m.explorer_search_opening()}
            </div>
        {/if}
    </div>

    <div class="search-results border rounded flex-grow-1">
        <div class="search-header row g-0 small fw-bold text-muted border-bottom px-2 py-2">
            <div class="col-5">{m.explorer_search_name_header()}</div>
            <div class="col-2">{m.explorer_search_type_header()}</div>
            <div class="col-2">{m.explorer_search_location_header()}</div>
            <div class="col-1">{m.explorer_search_owner_header()}</div>
            <div class="col-1">{m.explorer_search_updated_header()}</div>
            <div class="col-1">{m.explorer_search_created_header()}</div>
        </div>

        {#if explorerState.records.length === 0}
            <div class="d-flex align-items-center justify-content-center text-muted empty-results">
                {#if searchLoading}
                    <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                    {m.explorer_search_searching()}
                {:else if hasSearched}
                    {m.explorer_search_no_results()}
                {:else}
                    {m.explorer_search_results_placeholder()}
                {/if}
            </div>
        {:else}
            <VList
                style="height:calc(100% - 38px);"
                data={explorerState.records}
                getKey={(record) => record.id}
                itemSize={48}>
                {#snippet children(record)}
                    <div
                        class="search-row row g-0 align-items-center px-2 py-2 {explorerState.selectedRecords.includes(record.id) ? 'selected' : ''}"
                        role="button"
                        tabindex="0"
                        onclick={(clickEvent) => selectRecord(clickEvent, record.id)}
                        ondblclick={() => openRecord(record)}
                        onkeydown={(event) => event.key === "Enter" ? openRecord(record) : null}
                        data-record-id={record.id}>
                        <div class="col-5 text-truncate">
                            <i class="bi {iconMap[record.type] || 'bi-file-earmark'} me-2" aria-hidden="true"></i>
                            <span>{record.name || record.id}</span>
                            {#if record.star}
                                <i class="bi bi-star-fill text-warning ms-2" aria-label="Starred"></i>
                            {/if}
                            {#if record.locked}
                                <i class="bi bi-lock-fill text-muted ms-2" aria-label="Locked"></i>
                            {/if}
                        </div>
                        <div class="col-2 text-truncate text-capitalize">{record.type || "-"}</div>
                        <div class="col-2 text-truncate">{recordLocation(record)}</div>
                        <div class="col-1 text-truncate">{record.owner || "-"}</div>
                        <div class="col-1 text-truncate">{formatDate(record.updatedOn)}</div>
                        <div class="col-1 text-truncate">{formatDate(record.createdOn)}</div>
                    </div>
                {/snippet}
            </VList>
        {/if}
    </div>

    {#if hasMore}
        <div class="text-center mt-3">
            <button class="btn btn-sm btn-outline-primary" type="button" onclick={() => runSearch({ append: true })} disabled={searchLoading}>
                {#if searchLoading}
                    <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                {/if}
                {m.explorer_search_load_more()}
            </button>
        </div>
    {/if}
</div>

<style lang="css">
    #workspace-search-view {
        min-height: 0;
    }

    .search-results {
        min-height: 0;
        overflow: hidden;
        color: var(--bs-body-color);
        background-color: var(--bs-body-bg);
        border-color: var(--bs-border-color) !important;
    }

    .search-header {
        height: 38px;
        background-color: var(--bs-secondary-bg);
        border-color: var(--bs-border-color) !important;
    }

    .search-row {
        min-height: 48px;
        border-bottom: 1px solid var(--bs-border-color);
        cursor: pointer;
    }

    .search-row:hover {
        background-color: var(--bs-tertiary-bg);
    }

    .search-row.selected {
        color: var(--bs-body-color);
        background-color: rgba(var(--bs-primary-rgb), 0.08);
        box-shadow: inset 3px 0 0 var(--bs-primary);
    }

    .search-row.selected:hover {
        background-color: rgba(var(--bs-primary-rgb), 0.12);
    }

    .empty-results {
        height: calc(100% - 38px);
        min-height: 240px;
    }
</style>
